import React from 'react';
import { Link } from 'react-router-dom';
import PageSeo from '../components/PageSeo';
import { pocketBillingAiTermsSeo } from '../seo/pocketBillingAiSeo';

export default function PocketBillingAITerms() {
  return (
    <div className="min-h-screen bg-stone-50 text-stone-900">
      <PageSeo {...pocketBillingAiTermsSeo} />
      <header className="border-b border-stone-200 bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between gap-3">
          <Link to="/pocketbillingai" className="text-sm text-indigo-600 hover:text-indigo-800 font-medium">
            ← Back to PocketBillingAI
          </Link>
          <img src="/pocketbillingai-logo.png" alt="Pocket Billing AI" className="h-10 w-10 object-contain" />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-10 sm:py-14">
        <header className="mb-10">
          <p className="text-indigo-600 text-sm font-semibold uppercase tracking-wide mb-2">
            PocketBillingAI
          </p>
          <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-2">Terms &amp; Conditions</h1>
          <p className="text-stone-600">Subscription terms for shop owners</p>
          <p className="text-stone-400 text-sm mt-2">Last updated: June 2026</p>
        </header>

        <div className="space-y-8 text-stone-700 leading-relaxed text-sm sm:text-base">
          <section>
            <h2 className="text-lg font-semibold text-stone-900 mb-2">1. Agreement</h2>
            <p>
              By subscribing to or using PocketBillingAI (&quot;Service&quot;), you (&quot;Shop Owner&quot; or
              &quot;you&quot;) agree to these Terms &amp; Conditions with RestroAI (&quot;we&quot;, &quot;us&quot;). If
              you do not agree, do not use the Service.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-stone-900 mb-2">2. Service description</h2>
            <p>
              PocketBillingAI is offline-first Billing software for Shops, retailers, vendors, showrooms, restaurants, and similar businesses, including: billing,
              product/menu management, stock tracking, udhar (credit) accounts, sales &amp; profit reports, GST compliance
              tools, bill printing, WhatsApp sharing, optional Google Drive sync, and desktop + Android applications.
              Features may be updated or improved over time.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-stone-900 mb-2">3. Subscription &amp; pricing</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Plans (indicative): <strong>1 month — ₹500</strong>, <strong>6 months — ₹2,500</strong>,{' '}
                <strong>1 year — ₹5,000</strong> (prices inclusive of applicable tax unless stated otherwise).
              </li>
              <li>Promotional pricing is a limited-time offer and may change with notice on our website.</li>
              <li>Subscription is activated after payment confirmation and shop onboarding by our team.</li>
              <li>Plans are prepaid for the selected period. Renewal is at then-current rates unless a promotion applies.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-stone-900 mb-2">4. Payment &amp; refunds</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Payment is due before or at the start of each subscription period as agreed during onboarding.</li>
              <li>
                If the Service cannot be activated within 7 days of payment due to our fault, you may request a full
                refund.
              </li>
              <li>No partial refunds for unused days after activation, except where required by applicable law.</li>
              <li>Late or failed payment may result in suspension of subscription access until resolved.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-stone-900 mb-2">5. Your data</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Daily billing data is stored <strong>locally on your device</strong> (PC or Android). We do not host your
                transaction database on our servers.
              </li>
              <li>Optional Google Drive sync uses your Google account; you are responsible for Google account security.</li>
              <li>You are responsible for backing up data (e.g. regular Google sync or copying your database file).</li>
              <li>
                You must ensure customer data is collected and used in compliance with applicable privacy laws. See
                our{' '}
                <Link to="/pocketbillingai/privacy-policy" className="text-indigo-600 hover:underline">
                  Privacy Policy
                </Link>
                .
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-stone-900 mb-2">6. Acceptable use</h2>
            <p className="mb-2">You agree not to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Use the Service for unlawful purposes or fraudulent billing.</li>
              <li>Share, resell, or sublicense your subscription to unauthorised third parties.</li>
              <li>Reverse-engineer, copy, or redistribute the software without written permission.</li>
              <li>Attempt to bypass subscription or security controls.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-stone-900 mb-2">7. Account &amp; security</h2>
            <p>
              You are responsible for device security, staff access, and optional security PIN. Notify us promptly of
              unauthorised subscription use. We may suspend access for violation of these Terms.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-stone-900 mb-2">8. Intellectual property</h2>
            <p>
              PocketBillingAI software, branding, and documentation are owned by RestroAI. You receive a limited,
              non-exclusive licence to use the Service for your shop during an active subscription. You retain ownership
              of your business data (products, bills, customer records).
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-stone-900 mb-2">9. Disclaimers</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>The Service is provided &quot;as is&quot;. We do not guarantee uninterrupted or error-free operation.</li>
              <li>GST reports are aids for your records; you are responsible for filing accurate returns with tax authorities.</li>
              <li>We are not liable for business decisions, data entry errors, or losses from device failure without backup.</li>
              <li>
                Internet-dependent features (Google sync, cloud speech, WhatsApp links) require connectivity and
                third-party services.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-stone-900 mb-2">10. Limitation of liability</h2>
            <p>
              To the maximum extent permitted by law, RestroAI&apos;s total liability for any claim related to the
              Service shall not exceed the amount you paid for the subscription in the 12 months before the claim. We are
              not liable for indirect, incidental, or consequential damages.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-stone-900 mb-2">11. Termination</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>You may stop using the Service when your subscription period ends without renewal.</li>
              <li>We may terminate or suspend access for breach of Terms, non-payment, or abuse, with notice where practicable.</li>
              <li>After termination, locally stored data remains on your device; cloud sync is your responsibility.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-stone-900 mb-2">12. Changes</h2>
            <p>
              We may update these Terms or pricing with notice on{' '}
              <Link to="/pocketbillingai" className="text-indigo-600 hover:underline">
                www.restroai.in/pocketbillingai
              </Link>
              . Continued use after changes constitutes acceptance. Material price increases apply to renewals, not the
              current prepaid period.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-stone-900 mb-2">13. Governing law</h2>
            <p>
              These Terms are governed by the laws of India. Disputes shall be subject to the courts of competent
              jurisdiction in India.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-stone-900 mb-2">14. Contact</h2>
            <p>Questions about PocketBillingAI, pricing, or these Terms:</p>
            <ul className="list-none mt-2 space-y-1">
              <li>
                <strong>Website:</strong>{' '}
                <Link to="/pocketbillingai" className="text-indigo-600 hover:underline">
                  www.restroai.in/pocketbillingai
                </Link>
              </li>
              <li>
                <strong>Contact:</strong>{' '}
                <Link to="/contact-us" className="text-indigo-600 hover:underline">
                  RestroAI Contact Us
                </Link>
              </li>
            </ul>
          </section>
        </div>

        <footer className="mt-12 pt-8 border-t border-stone-200 text-stone-500 text-sm text-center">
          <p>© {new Date().getFullYear()} RestroAI · PocketBillingAI</p>
          <p className="mt-2 flex flex-wrap justify-center gap-x-3 gap-y-1">
            <Link to="/pocketbillingai" className="text-indigo-600 hover:underline">
              Home
            </Link>
            <Link to="/pocketbillingai/privacy-policy" className="text-indigo-600 hover:underline">
              Privacy Policy
            </Link>
            <Link to="/contact-us" className="text-indigo-600 hover:underline">
              Contact
            </Link>
          </p>
        </footer>
      </main>
    </div>
  );
}
